import { Injectable } from '@angular/core'
import { LocalStorageService } from '@mistergreen/shared/angular-injectables/local-storage/local-storage.service'
import { HttpHeaders } from '@angular/common/http'
import { InMemoryCache } from '@apollo/client/cache'
import { onError } from '@apollo/client/link/error'
import { ApolloLink } from '@apollo/client/core'
import { Apollo } from 'apollo-angular'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { HttpBatchLink, HttpLink } from 'apollo-angular/http'
import { EnvironmentService } from '../environment/environment.service'
import introspectionQueryResultData from '../../generated/introspection'
import { TenantService } from '../tenant/tenant.service'
import { LocaleService } from '../locale/services/locale.service'

/*
   List of operation names of GraphQL queries which,
   for performance reasons, shouldn't be batched.
 */
const nonBatchableOperations = ['DealContractPdf']

@UntilDestroy()
@Injectable()
export class GraphqlClientFactory {
    private cache: InMemoryCache
    private tenant: string

    constructor(
        private localStorageService: LocalStorageService,
        private httpLink: HttpLink,
        private httpBatchLink: HttpBatchLink,
        private apollo: Apollo,
        private environmentService: EnvironmentService,
        private tenantService: TenantService,
        private localeService: LocaleService
    ) {
        // get token and future changes to it (even in different window)
        this.localStorageService
            .getItemObservable('token')
            .pipe(untilDestroyed(this))
            .subscribe(token => (this.token = token))

        this.produceClient()

        this.tenantService.tenant$.pipe(untilDestroyed(this)).subscribe(tenant => (this.tenant = tenant))
    }

    private _token: string | null = null

    /**
     *
     * @returns {string|null}
     */
    private get token(): string | null {
        return this._token
    }

    /**
     *
     * @param {string} value
     */
    private set token(value: string | null) {
        this._token = value
    }

    /**
     * Initializes the Apollo Client
     */
    private produceClient() {
        // create linkTarget chain
        const links: ApolloLink[] = []

        // provide authentication header
        links.push(this.createAuthLink())

        // handle generic errors
        links.push(this.createErrorLink())

        // create the server linkTarget
        links.push(this.createHttpLink())

        // create caching store
        this.cache = this.createCache()

        // Do not create the Apollo client multiple times when this service is injected multiple times
        // create default apollo client
        this.apollo.create({
            link: links.length === 1 ? links[0] : ApolloLink.from(links),
            cache: this.cache,
            connectToDevTools: !this.environmentService.environment?.production,
            name: 'public',
            version: '1.0',
        })
    }

    private createCache() {
        return new InMemoryCache({
            possibleTypes: introspectionQueryResultData.possibleTypes,
        })
    }

    private createAuthLink() {
        return new ApolloLink((operation, forward) => {
            if (this.tenant || this.token) {
                // add the authorization & tenant to the headers
                let headers = new HttpHeaders()

                if (this.tenant) {
                    headers = headers.set('tenant', this.tenant)
                }

                if (this.localeService && this.localeService.locale && this.localeService.language) {
                    headers = headers.set(
                        'Accept-Language',
                        `${this.localeService.locale}, ${this.localeService.language};q=0.9, en;q=0.7, *;q=0.5`
                    )
                }

                if (this.token) {
                    headers = headers.set('Authorization', `Bearer ${this.token}`)
                }

                operation.setContext({ headers })
            }

            return forward(operation)
        })
    }

    private createErrorLink() {
        return onError(({ graphQLErrors }) => {
            if (graphQLErrors) {
                graphQLErrors.forEach(({ message }) => {
                    // get rid of token if user is not authenticated
                    if (message.indexOf('User is not authenticated') >= 0 && this.token) {
                        this.localStorageService.setItem('token', null)
                    }
                })
            }
        })
    }

    private createHttpLink() {
        const uri = this.environmentService.environment.gateway.v3.graphql

        // create http linkTarget
        const apolloHttpLink = ApolloLink.split(
            operation => nonBatchableOperations.includes(operation.operationName),
            this.httpLink.create({ uri }),
            this.httpBatchLink.create({ uri })
        )

        return apolloHttpLink
    }
}
